import { useMemo } from 'react';

import { type IconKey } from '@/content/site';
import { useImportTaxSettings } from '@/hooks/useImportTaxSettings';
import { transportBandLabel, transportBandsFor, transportDestinations } from '@/services/tax';

import {
  destinationFallback,
  destinationMeta,
  enquiryOnlyDestinations,
} from '../content';

const DESTINATION_ORDER = [
  'Kampala, Uganda',
  'Bunagana, DR Congo',
  'Nimule, South Sudan',
  'Katuna, Uganda',
] as const;
const NAMED_DESTINATIONS: readonly string[] = DESTINATION_ORDER;

/** Only these named routes use the published Mombasa transport bands. */
const PUBLISHED_TARIFF_DESTINATIONS = new Set<string>([
  'Kampala, Uganda',
  'Bunagana, DR Congo',
  'Nimule, South Sudan',
]);

export type DeliveryWeightBand = {
  /** "1,500–1,999 kg — large SUVs and pickups", as the calculator words it. */
  label: string;
  amountUsd: number;
};

export type DeliveryRoute = {
  /** The destination exactly as the settings name it; also the tab's key. */
  destination: string;
  /** "Kampala" — the tab label, since repeating the country is noise. */
  place: string;
  /** "Uganda" — restored underneath, so the tab stays unambiguous. */
  country: string;
  icon: IconKey;
  note: string;
  /** Published Mombasa tariff is restricted to the approved named routes. */
  pricing: 'published' | 'enquiry';
  /** The cheapest band, which is what a "from" figure means. */
  fromUsd: number | null;
  bands: readonly DeliveryWeightBand[];
};

type Routes = {
  routes: readonly DeliveryRoute[];
  /** The date the figures came from, shown so the panel can be dated. */
  effectiveFrom: string;
  /** True when the live settings could not be read and the bundled copy is in use. */
  isFallback: boolean;
};

/**
 * The named destinations, with published tariffs only for approved routes.
 *
 * Derived from the same import tax settings the Drive-Home Cost calculator
 * reads. Only the three named tariff destinations use those Mombasa bands;
 * other configured places remain enquiry-led. This protects new destinations
 * from inheriting a price for a route Kabex has not published.
 *
 * `useImportTaxSettings` answers synchronously with the bundled figures while
 * the live ones load, so this band has no loading state and prerenders with
 * real content.
 */
export function useDeliveryRoutes(): Routes {
  const { settings, isFallback } = useImportTaxSettings();

  const routes = useMemo(
    () => {
      const enquiryOnlyNames = new Set<string>(enquiryOnlyDestinations);
      const destinations = [
        ...new Set([...NAMED_DESTINATIONS, ...transportDestinations(settings.transport)]),
      ];
      const configuredRoutes = destinations
        .filter((destination) => !enquiryOnlyNames.has(destination))
        .map((destination) => {
          const [place, country] = destination.split(',');
          const meta = destinationMeta[destination] ?? destinationFallback;
          const hasPublishedTariff = PUBLISHED_TARIFF_DESTINATIONS.has(destination);
          const sourceBands = hasPublishedTariff
            ? transportBandsFor(settings.transport, destination)
            : [];
          const pricing: DeliveryRoute['pricing'] =
            hasPublishedTariff && sourceBands.length > 0 ? 'published' : 'enquiry';

          return {
            destination,
            place: place.trim(),
            // A destination written without a country keeps the whole string
            // rather than showing an empty line under the tab.
            country: country?.trim() ?? destination,
            icon: meta.icon,
            note: meta.note,
            pricing,
            fromUsd: sourceBands.length
              ? Math.min(...sourceBands.map((band) => band.amountUsd))
              : null,
            bands: sourceBands.map((band) => ({
              label: transportBandLabel(band),
              amountUsd: band.amountUsd,
            })),
          };
        });
      const enquiryRoutes = enquiryOnlyDestinations.map((destination) => {
        const [place, country] = destination.split(',');
        const meta = destinationMeta[destination] ?? destinationFallback;

        return {
          destination,
          place: place.trim(),
          country: country?.trim() ?? destination,
          icon: meta.icon,
          note: meta.note,
          pricing: 'enquiry' as const,
          fromUsd: null,
          bands: [],
        };
      });
      const priority = (destination: string) => {
        const rank = DESTINATION_ORDER.indexOf(destination as (typeof DESTINATION_ORDER)[number]);
        return rank < 0 ? DESTINATION_ORDER.length : rank;
      };

      return [...configuredRoutes, ...enquiryRoutes].sort(
        (a, b) => priority(a.destination) - priority(b.destination),
      );
    },
    [settings.transport],
  );

  return { routes, effectiveFrom: settings.effectiveFrom, isFallback };
}
